import React, { useState } from "react";
import axios from "axios";
import { createGlobalStyle } from "styled-components";
import { useNavigate } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";

const Global = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0ebe3; font-family: 'Malgun Gothic','Apple SD Gothic Neo',Arial,sans-serif; }
`;

const API = process.env.REACT_APP_API_URL || "";

const SERVICE_TYPES = [
  "종합운세", "신년운세", "연애운", "결혼운",
  "직업운", "사업운", "건강운", "연애궁합", "심층궁합",
];

const wrap: React.CSSProperties = {
  maxWidth: 520, margin: "0 auto", padding: "32px 16px 80px",
};
const card: React.CSSProperties = {
  background: "#fff", borderRadius: 20, border: "1.5px solid #e0d5c8",
  padding: "28px 24px", boxShadow: "0 4px 24px rgba(100,70,40,0.08)",
};
const label: React.CSSProperties = {
  display: "block", fontSize: "0.78rem", fontWeight: 700,
  color: "#7a6654", marginBottom: 6,
};
const input: React.CSSProperties = {
  width: "100%", padding: "10px 12px", border: "1.5px solid #e0d5c8",
  borderRadius: 10, fontSize: "0.88rem", color: "#3d2b1f",
  background: "#fff", fontFamily: "inherit", marginBottom: 14,
};
const inputDisabled: React.CSSProperties = {
  ...input, background: "#f5f0ea", color: "#b0a090", cursor: "not-allowed",
};
const btn: React.CSSProperties = {
  width: "100%", padding: "14px", background: "linear-gradient(135deg,#6b4c35,#3d2b1f)",
  color: "#fff", border: "none", borderRadius: 14,
  fontSize: "1rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
  boxShadow: "0 4px 16px rgba(61,43,31,0.2)",
};

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) {
    const [y, m, d] = val.split(sep).map(Number);
    return { year: y || 0, month: m || 0, day: d || 0 };
  }
  if (val.length === 8) {
    return { year: Number(val.slice(0,4)), month: Number(val.slice(4,6)), day: Number(val.slice(6,8)) };
  }
  return { year: 0, month: 0, day: 0 };
}

const COMPAT_SERVICES = ["연애궁합", "심층궁합", "재회운", "결혼운"];

const emptyForm = () => ({
  name: "", email: "", phone: "", gender: "female",
  birth_date: "", birth_type: "solar",
  birth_time: "", birth_time_unknown: false,
  city: "서울특별시",
  service_type: "종합운세", memo: "", consent: false,
});

const emptyPartner = () => ({
  name: "", gender: "female",
  birth_date: "", birth_type: "solar",
  birth_time: "", birth_time_unknown: false,
  city: "서울특별시",
});

export default function VisitPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [partner, setPartner] = useState(emptyPartner());
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [done,    setDone]    = useState(false);

  const isCompat = COMPAT_SERVICES.includes(form.service_type);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));
  const setP = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setPartner(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setError("개인정보 활용에 동의해주세요."); return; }
    if (!form.name || !form.birth_date) {
      setError("필수 항목(이름, 생년월일)을 모두 입력해주세요."); return;
    }
    const { year, month, day } = parseBirthDate(form.birth_date);
    if (!year || !month || !day) {
      setError("생년월일을 YYYY/MM/DD 형식으로 입력해주세요. 예) 1990/05/15"); return;
    }
    if (!form.email) { setError("이메일 주소를 입력해주세요. 리포트 발송에 필요합니다."); return; }
    if (isCompat) {
      const pd = parseBirthDate(partner.birth_date);
      if (!partner.name || !pd.year) {
        setError("상대방 이름과 생년월일을 입력해주세요."); return;
      }
    }
    setLoading(true); setError("");
    const pd = parseBirthDate(partner.birth_date);
    const partnerPayload = isCompat ? {
      name: partner.name,
      gender: partner.gender,
      birth_year: pd.year, birth_month: pd.month, birth_day: pd.day,
      birth_hour: partner.birth_time_unknown ? 99 : parseTime(partner.birth_time).hour,
      birth_minute: partner.birth_time_unknown ? 0 : parseTime(partner.birth_time).minute,
      birth_type: partner.birth_type,
      city: partner.city,
      compatibility_type: form.service_type === "심층궁합" ? "deep" : "casual",
    } : null;
    try {
      await axios.post(`${API}/api/v1/visit/submit`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        gender: form.gender,
        birth_year: year, birth_month: month, birth_day: day,
        birth_hour: form.birth_time_unknown ? 99 : parseTime(form.birth_time).hour,
        birth_minute: form.birth_time_unknown ? 0 : parseTime(form.birth_time).minute,
        birth_type: form.birth_type,
        city: form.city,
        service_type: form.service_type,
        partner_info: partnerPayload,
        memo: form.memo,
        consent: true,
      });
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || "제출 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <>
        <Global />
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: "2.4rem" }}>🔮</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#3d2b1f", marginTop: 6 }}>우연 사주</div>
          </div>
          <div style={{ ...card, textAlign: "center" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: "1.1rem", fontWeight: 900, color: "#3d2b1f", marginBottom: 10 }}>
              방문 상담 접수 완료
            </div>
            <div style={{ fontSize: "0.85rem", color: "#7a6654", lineHeight: 1.8 }}>
              접수가 완료되었습니다.<br />
              {form.email && <>상담 후 분석 리포트를 <strong>{form.email}</strong>으로 보내드리겠습니다.<br /></>}
              문의: wooyeonsaju@gmail.com
            </div>
            <button onClick={() => { setDone(false); setForm(emptyForm()); setPartner(emptyPartner()); }}
              style={{ ...btn, marginTop: 20, background: "linear-gradient(135deg,#c09a6b,#a07850)" }}>
              새 접수하기
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Global />
      <div style={wrap}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <button onClick={() => navigate("/")}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.7)", border: "1.5px solid #e0d5c8", borderRadius: 20, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#7a6654", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(100,70,40,0.08)" }}>
            ← 홈
          </button>
          <div style={{ flex: 1, textAlign: "right", fontSize: 15, fontWeight: 900, color: "#3d2b1f" }}>🏠 방문 상담 사주</div>
        </div>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: "2rem", marginBottom: 6 }}>🏠</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#3d2b1f", marginTop: 6 }}>방문 상담 사주</div>
          <div style={{ fontSize: "0.8rem", color: "#9c7b5a", marginTop: 4 }}>1:1 대면 상담 후 명리학 분석 리포트 이메일 발송</div>
        </div>

        <div style={card}>
          <div style={{ display: "inline-block", padding: "5px 14px", background: "#6b4c3520", border: "1.5px solid #6b4c3540", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700, color: "#6b4c35", marginBottom: 20 }}>
            🏠 방문 상담 — 상담 후 리포트를 이메일로 발송해드립니다
          </div>

          <form onSubmit={handleSubmit}>
            {/* 이름 + 성별 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={label}>이름 *</label>
                <input style={input} value={form.name} onChange={set("name")} placeholder="홍길동" required />
              </div>
              <div>
                <label style={label}>성별 *</label>
                <select style={input} value={form.gender} onChange={set("gender")}>
                  <option value="female">여성</option>
                  <option value="male">남성</option>
                </select>
              </div>
            </div>

            {/* 생년월일 — 한 칸 */}
            <label style={label}>생년월일 * (예: 1990/05/15)</label>
            <input style={input} value={form.birth_date} onChange={set("birth_date")} placeholder="1990/05/15" maxLength={10} />

            {/* 양음력 */}
            <label style={label}>양/음력</label>
            <select style={input} value={form.birth_type} onChange={set("birth_type")}>
              <option value="solar">양력</option>
              <option value="lunar">음력</option>
              <option value="lunar_leap">음력 윤달</option>
            </select>

            {/* 태어난 시간 */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <label style={{ ...label, marginBottom: 0 }}>태어난 시간</label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer" }}>
                <input type="checkbox" checked={form.birth_time_unknown}
                  onChange={e => setForm(f => ({ ...f, birth_time_unknown: e.target.checked }))}
                  style={{ accentColor: "#c09a6b" }} />
                시간 모름
              </label>
            </div>
            <input
              style={form.birth_time_unknown ? inputDisabled : input}
              placeholder="예: 14:30 또는 1430"
              maxLength={5}
              value={form.birth_time_unknown ? "" : form.birth_time}
              disabled={form.birth_time_unknown}
              onChange={set("birth_time")} />

            {/* 출생 도시 */}
            <label style={label}>출생 도시 (시간 보정용)</label>
            <div style={{ marginBottom: 14 }}>
              <CityAutocomplete value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
            </div>

            {/* 이메일 + 전화 */}
            <label style={label}>이메일 (리포트 수령용) *</label>
            <input style={input} type="email" value={form.email} onChange={set("email")} placeholder="example@email.com" required />
            <label style={label}>전화번호</label>
            <input style={input} value={form.phone} onChange={set("phone")} placeholder="010-0000-0000" />

            {/* 서비스 종류 */}
            <label style={label}>상담 종류 *</label>
            <select style={input} value={form.service_type} onChange={set("service_type")}>
              {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            {/* 상대방 정보 (궁합 서비스 선택 시) */}
            {isCompat && (
              <div style={{ marginBottom: 14, padding: "16px", background: "#fff4f8", border: "1.5px solid #ffbfd6", borderRadius: 12 }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#c2255c", marginBottom: 14 }}>
                  💕 상대방 정보 입력
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={label}>상대방 이름 *</label>
                    <input style={input} value={partner.name} onChange={setP("name")} placeholder="홍길동" />
                  </div>
                  <div>
                    <label style={label}>상대방 성별 *</label>
                    <select style={input} value={partner.gender} onChange={setP("gender")}>
                      <option value="female">여성</option>
                      <option value="male">남성</option>
                    </select>
                  </div>
                </div>
                <label style={label}>상대방 생년월일 * (예: 1990/05/15)</label>
                <input style={input} value={partner.birth_date} onChange={setP("birth_date")} placeholder="1990/05/15" maxLength={10} />
                <label style={label}>상대방 양/음력</label>
                <select style={input} value={partner.birth_type} onChange={setP("birth_type")}>
                  <option value="solar">양력</option>
                  <option value="lunar">음력</option>
                  <option value="lunar_leap">음력 윤달</option>
                </select>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <label style={{ ...label, marginBottom: 0 }}>상대방 태어난 시간</label>
                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer" }}>
                    <input type="checkbox" checked={partner.birth_time_unknown}
                      onChange={e => setPartner(p => ({ ...p, birth_time_unknown: e.target.checked }))}
                      style={{ accentColor: "#c09a6b" }} />
                    시간 모름
                  </label>
                </div>
                <input
                  style={partner.birth_time_unknown ? inputDisabled : input}
                  placeholder="예: 14:30 또는 1430"
                  maxLength={5}
                  value={partner.birth_time_unknown ? "" : partner.birth_time}
                  disabled={partner.birth_time_unknown}
                  onChange={setP("birth_time")} />
                <label style={label}>상대방 출생 도시</label>
                <div style={{ marginBottom: 14 }}>
                  <CityAutocomplete value={partner.city} onChange={v => setPartner(p => ({ ...p, city: v }))} />
                </div>
              </div>
            )}

            {/* 메모 */}
            <label style={label}>상담 요청 내용 (선택)</label>
            <textarea style={{ ...input, height: 80, resize: "vertical" }}
              value={form.memo} onChange={set("memo")}
              placeholder="궁금하신 점이나 원하시는 상담 내용을 간단히 적어주세요." />

            {/* 동의 */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "0.78rem", color: "#7a6654", marginBottom: 20, cursor: "pointer" }}>
              <input type="checkbox" checked={form.consent}
                onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                style={{ marginTop: 2, accentColor: "#c09a6b" }} />
              <span>개인정보(생년월일, 이메일 등)를 사주 분석 및 서비스 제공 목적으로 수집·이용하는 것에 동의합니다. *</span>
            </label>

            {error && (
              <div style={{ padding: "10px 14px", background: "#fff5f5", border: "1px solid #ffc9c9", borderRadius: 10, fontSize: "0.82rem", color: "#c92a2a", marginBottom: 14 }}>
                {error}
              </div>
            )}
            <button type="submit" style={btn} disabled={loading}>
              {loading ? "접수 중..." : "방문 상담 접수하기"}
            </button>
          </form>
        </div>

        <div style={{ marginTop: 16, padding: "14px 16px", background: "#faf5ef", borderRadius: 14, border: "1px solid #e8d8c4", fontSize: "0.78rem", color: "#9c7b5a", lineHeight: 1.8 }}>
          · 접수 후 상담 시 추가 정보를 확인할 수 있습니다.<br />
          · 분석 리포트는 상담 완료 후 이메일로 발송됩니다.<br />
          · 문의: wooyeonsaju@gmail.com
        </div>
        <SiteFooter />
      </div>
    </>
  );
}
